import Vue from 'vue'
import VueRouter from 'vue-router'
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta'
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress/nprogress'
import store from '@state/store'
import { allRoutes } from './routes'
import empresaService from '@/src/services/empresa_sistema'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css';

Vue.component('v-select', vSelect)
Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

const router = new VueRouter({
  routes: allRoutes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // If this isn't an initial page load...
  if (routeFrom.name !== null) {
    // Start the route progress bar.
    NProgress.start()
  }

  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired)

  // If auth isn't required for the route, just continue.
  if (!authRequired) return next()

  // If auth is required and the user is logged in...
  if (store.getters['auth/loggedIn']) {
    // Validate the local user token...
    return store.dispatch('auth/validate').then(async (validUser) => {
      const empresa169 = validUser ? await isEmpresa169(validUser) : false
      if (validUser && empresa169) {
        if (!isAllowedPathForEmpresa169(routeTo.path)) {
          return next({ name: 'Dashboard' })
        }
      }

      // Then continue if the token still represents a valid user,
      // otherwise redirect to login.
      validUser ? next() : redirectToLogin()
    })
  }

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  redirectToLogin()

  function redirectToLogin() {
    // Pass the original route to the login component
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } })
  }
})

function getEmpresaId(user) {
  if (!user) return null

  const candidates = [
    user.id_empresa_sistema,
    user.id_empresa,
    user.idEmpresa,
    user.empresa_id,
    user.idEmpresaSistema,
  ]

  for (let i = 0; i < candidates.length; i += 1) {
    const parsed = Number(candidates[i])
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed
    }
  }

  return null
}

async function isEmpresa169(user) {
  if (window.localStorage.getItem('modo_demo_169') === '1') {
    return true
  }

  const idFromUser = getEmpresaId(user)
  if (idFromUser) {
    if (idFromUser === 169) {
      window.localStorage.setItem('modo_demo_169', '1')
      return true
    }
    window.localStorage.removeItem('modo_demo_169')
    return false
  }

  try {
    const token = user && user.token ? user.token : ''
    if (!token) return false
    const response = await empresaService.listarEmpresa(token)
    const empresa =
      response && response.data && response.data.empresa && response.data.empresa[0]
        ? response.data.empresa[0]
        : null
    const empresaId = empresa ? Number(empresa.id_empresa_sistema) : null
    if (empresaId === 169) {
      window.localStorage.setItem('modo_demo_169', '1')
      return true
    }
  } catch (error) {
    return false
  }

  window.localStorage.removeItem('modo_demo_169')
  return false
}

function isAllowedPathForEmpresa169(path) {
  const allowedPaths = [
    '/',
    '/logout',
  ]

  return allowedPaths.includes(path)
}

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  NProgress.done()
})

export default router
